import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import LinkIcon from '@material-design-icons/svg/filled/link.svg?react';
import ScienceIcon from '@material-design-icons/svg/filled/science.svg?react';
import { isLeft } from 'fp-ts/Either';
import { useState } from 'react';
import { toast } from 'sonner';

import { SearchableSelect } from '../../../components/SearchableSelect';
import { Button } from '../../../components/ui/button';
import { ExperimentCard } from '../components/ExperimentCard';
import { Section } from '../components/Section';
import type { AppSpotlightController } from '../useAppSpotlight';
import type { ExperimentPrefill, SpotlightAppChoice } from '../types';
import { packageKey } from '../utils';

const MIN_WHITELIST = 4;
const MAX_WHITELIST = 150;

interface ExperimentsTabProps {
  controller: AppSpotlightController;
  prefill: ExperimentPrefill | null;
}

export function ExperimentsTab({ controller, prefill }: ExperimentsTabProps) {
  const [experimentId, setExperimentId] = useState('');
  const [weekId, setWeekId] = useState(prefill?.weekId ?? '');
  const [draftId] = useState<string | null>(prefill?.draftId ?? null);
  const [whitelist, setWhitelist] = useState<string[]>(
    prefill?.whitelistAndroidPackages ?? [],
  );
  const [notes, setNotes] = useState('');
  const [pickerKey, setPickerKey] = useState(0);

  const dappNameByPackage = new Map<string, string>();
  for (const app of controller.appOptions) {
    if (app.packageName) {
      dappNameByPackage.set(packageKey(app.packageName), app.dappName);
    }
  }

  const selected = new Set(whitelist.map(packageKey));
  const choices: SpotlightAppChoice[] = controller.appOptions
    .filter((app) => app.packageName)
    .map((app) => ({
      key: app.packageName ?? '',
      label: `${app.dappName} (${app.packageName})`,
      packageName: app.packageName ?? '',
    }))
    .filter((choice) => !selected.has(packageKey(choice.key)));

  const addPackage = (choice?: SpotlightAppChoice) => {
    if (!choice) {
      return;
    }
    setWhitelist((current) =>
      current.some((pkg) => packageKey(pkg) === packageKey(choice.packageName))
        ? current
        : [...current, choice.packageName],
    );
    setPickerKey((current) => current + 1);
  };

  const removePackage = (pkg: string) => {
    setWhitelist((current) => current.filter((item) => item !== pkg));
  };

  const canCreate =
    experimentId.trim().length > 0 &&
    weekId.trim().length > 0 &&
    whitelist.length >= MIN_WHITELIST &&
    whitelist.length <= MAX_WHITELIST;

  const handleCreate = async () => {
    if (!canCreate) {
      return;
    }
    const result = await controller.createExperiment({
      experimentId: experimentId.trim(),
      weekId: weekId.trim(),
      draftId,
      whitelistAndroidPackages: whitelist,
      notes: notes.trim() || null,
    });
    if (isLeft(result)) {
      toast.error(result.left.message);
      return;
    }
    toast.success('Experiment created');
    setExperimentId('');
    setWhitelist([]);
    setNotes('');
  };

  const handleApprove = async (experimentDbId: string) => {
    const result = await controller.approveExperiment(experimentDbId);
    if (isLeft(result)) {
      toast.error(result.left.message);
      return;
    }
    toast.success('Experiment approved');
  };

  const handleGenerate = async (
    experimentDbId: string,
    audienceMode: 'limited' | 'all',
    userLimit?: number,
  ) => {
    const result = await controller.runPersonalization(
      experimentDbId,
      audienceMode,
      userLimit,
    );
    if (isLeft(result)) {
      toast.error(result.left.message);
      return;
    }
    toast.success('Personalization run queued');
  };

  return (
    <div className="space-y-4">
      <Section
        title="New experiment"
        icon={<ScienceIcon className="size-4 fill-current" />}
        collapsible
      >
        <div className="space-y-4">
          {prefill && (
            <div className="flex items-center gap-2 rounded-lg border border-teal-500/20 bg-teal-500/5 px-3 py-2 text-xs text-teal-200">
              <LinkIcon className="size-3.5 fill-current" />
              Prefilled from spotlight · week {prefill.weekId} ·{' '}
              {prefill.whitelistAndroidPackages.length} apps
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-neutral-400">
                Experiment ID
              </span>
              <input
                aria-label="Experiment ID"
                value={experimentId}
                onChange={(event) => setExperimentId(event.target.value)}
                placeholder="2026-W22-personalization"
                className="h-11 w-full rounded-xl border border-neutral-800 bg-neutral-900/50 px-4 text-sm text-white placeholder:text-neutral-600 focus:border-teal-500/60 focus:outline-none"
              />
            </label>
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-neutral-400">
                Week ID
              </span>
              <input
                aria-label="Experiment Week ID"
                value={weekId}
                onChange={(event) => setWeekId(event.target.value)}
                placeholder="2026-W22"
                className="h-11 w-full rounded-xl border border-neutral-800 bg-neutral-900/50 px-4 text-sm text-white placeholder:text-neutral-600 focus:border-teal-500/60 focus:outline-none"
              />
            </label>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-neutral-400">
                Whitelist apps
              </span>
              <span
                className={
                  whitelist.length < MIN_WHITELIST ||
                  whitelist.length > MAX_WHITELIST
                    ? 'text-xs text-amber-400'
                    : 'text-xs text-neutral-500'
                }
              >
                {whitelist.length} selected ({MIN_WHITELIST}–{MAX_WHITELIST})
              </span>
            </div>
            <SearchableSelect
              key={pickerKey}
              choices={choices}
              className="h-11 bg-neutral-900/50"
              placeholder={
                controller.isLoadingApps ? 'Loading apps…' : 'Add an app'
              }
              searchPlaceholder="Search apps…"
              disabled={controller.isLoadingApps || choices.length === 0}
              onChange={addPackage}
            />
            {whitelist.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {whitelist.map((pkg) => (
                  <span
                    key={pkg}
                    className="flex items-center gap-1.5 rounded-full border border-neutral-700 bg-neutral-900 py-1 pl-3 pr-1.5 text-xs text-neutral-200"
                  >
                    <span className="max-w-[220px] truncate">
                      {dappNameByPackage.get(packageKey(pkg)) ?? pkg}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove ${pkg}`}
                      onClick={() => removePackage(pkg)}
                      className="flex size-4 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-800 hover:text-white"
                    >
                      <CloseIcon className="size-3 fill-current" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-neutral-400">
              Notes (optional)
            </span>
            <textarea
              aria-label="Experiment notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={2}
              maxLength={1000}
              className="w-full rounded-xl border border-neutral-800 bg-neutral-900/50 px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:border-teal-500/60 focus:outline-none"
            />
          </label>

          <div className="flex justify-end">
            <Button
              onClick={() => void handleCreate()}
              disabled={!canCreate}
              className="bg-teal-600 text-white hover:bg-teal-500"
            >
              Create experiment
            </Button>
          </div>
        </div>
      </Section>

      {controller.experiments.length === 0 ? (
        <div className="rounded-xl border border-dashed border-neutral-800 px-6 py-12 text-center text-sm text-neutral-500">
          No experiments yet.
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {controller.experiments.map((experiment) => (
            <ExperimentCard
              key={experiment.id}
              experiment={experiment}
              isRunningPersonalization={controller.isRunningPersonalization}
              onApprove={(item) => void handleApprove(item.id)}
              onGeneratePersonalization={(item, mode, limit) =>
                void handleGenerate(item.id, mode, limit)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
